import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, imgSrc, formatPrice, CATEGORIES, CONTRACTS, categoryLabel, contractLabel, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, LogOut, Eye, EyeOff, Star, Upload, X, Rss, MapPin, FileText, GripVertical, ChevronLeft, ChevronRight, Languages } from "lucide-react";
import Seo from "@/components/Seo";

const emptyForm = {
  title: "", contract: "vendita", category: "appartamenti", price: "", zone: "", address: "", city: "Roma",
  lat: "", lng: "", mq: "", rooms: "", bedrooms: "", bathrooms: "", floor: "", energy_class: "",
  elevator: "", kitchen: "", terrace: "", balcony: "", air_conditioning: "", heating: "", condition: "", price_on_request: false,
  description: "", features: "", images: [], floor_plan: "", badge: "", featured: false, visible: true,
};

// Suggerimenti (scrivibili liberamente) per i campi caratteristiche
const KITCHEN_OPTS = ["Abitabile", "Semi-abitabile", "Angolo cottura", "Cucina a vista", "Cucina separata", "Cucinotto"];
const AC_OPTS = ["Autonomo, freddo/caldo", "Centralizzato", "Predisposizione", "Assente"];
const HEATING_OPTS = ["Autonomo", "Centralizzato", "Autonomo, a metano", "Autonomo, ad aria", "Pavimento", "Assente"];

const inputCls = "w-full bg-white border border-[#E0E3E6] focus:border-[#000000] outline-none px-4 py-3 text-sm transition-colors";
const labelCls = "block text-[10px] font-semibold uppercase tracking-[0.18em] text-[#2E2E2E] mb-1.5";

// Campo numerico con pulsanti − / + : accetta anche il "+" finale (es. "3+")
const StepperInput = ({ value, onChange, testid, step = 1, min = 0 }) => {
  const str = String(value ?? "");
  const num = parseInt(str, 10) || 0;
  const hasPlus = str.trim().endsWith("+");
  const bump = (d) => {
    const n = Math.max(min, num + d);
    onChange(`${n}${hasPlus ? "+" : ""}`);
  };
  const handleType = (raw) => {
    if (/^\d*\+?$/.test(raw)) onChange(raw);
  };
  return (
    <div className="flex items-stretch">
      <button type="button" data-testid={`${testid}-minus`} onClick={() => bump(-step)} disabled={num <= min} aria-label="Diminuisci"
        className="w-11 shrink-0 border border-r-0 border-[#E0E3E6] bg-white text-lg font-medium hover:bg-[#F1F2F4] active:bg-[#E8E8E8] disabled:opacity-30 transition-colors">−</button>
      <input data-testid={testid} type="text" inputMode="numeric" value={value} onChange={(e) => handleType(e.target.value)}
        className={`${inputCls} text-center`} placeholder="Es. 3 oppure 3+" />
      <button type="button" data-testid={`${testid}-plus`} onClick={() => bump(step)} aria-label="Aumenta"
        className="w-11 shrink-0 border border-l-0 border-[#E0E3E6] bg-white text-lg font-medium hover:bg-[#F1F2F4] active:bg-[#E8E8E8] transition-colors">+</button>
    </div>
  );
};

const toForm = (p) => ({
  ...emptyForm,
  ...p,
  price: p.price ?? "", mq: p.mq ?? "", rooms: p.rooms ?? "", bedrooms: p.bedrooms ?? "",
  bathrooms: p.bathrooms ?? "", floor: p.floor ?? "", energy_class: p.energy_class ?? "",
  elevator: p.elevator ?? "", kitchen: p.kitchen ?? "", terrace: p.terrace ?? "",
  balcony: p.balcony ?? "", air_conditioning: p.air_conditioning ?? "", heating: p.heating ?? "", condition: p.condition ?? "",
  price_on_request: p.price_on_request ?? false,
  lat: p.lat ?? "", lng: p.lng ?? "", badge: p.badge ?? "",
  features: (p.features || []).join(", "),
  images: p.images || [],
});

const toPayload = (f) => ({
  title: f.title, contract: f.contract, category: f.category,
  price: Number(f.price) || 0, zone: f.zone, address: f.address, city: f.city?.trim() || "Roma",
  lat: f.lat === "" ? null : Number(f.lat), lng: f.lng === "" ? null : Number(f.lng),
  mq: f.mq === "" ? null : Number(f.mq),
  // locali/camere/bagni: stringa, può contenere "+" (es. "3+")
  rooms: String(f.rooms ?? "").trim() || null,
  bedrooms: String(f.bedrooms ?? "").trim() || null,
  bathrooms: String(f.bathrooms ?? "").trim() || null,
  floor: f.floor || null, energy_class: f.energy_class || null,
  elevator: f.elevator || null, kitchen: f.kitchen?.trim() || null, terrace: f.terrace || null,
  balcony: f.balcony || null, air_conditioning: f.air_conditioning?.trim() || null, heating: f.heating?.trim() || null,
  condition: f.condition || null,
  price_on_request: !!f.price_on_request,
  description: f.description,
  features: f.features.split(",").map((s) => s.trim()).filter(Boolean),
  images: f.images, floor_plan: f.floor_plan || null, badge: f.badge || null, featured: f.featured, visible: f.visible,
});

const LoginForm = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Bentornato in ROMACASA");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] flex items-center justify-center px-5 noise" data-testid="admin-login-page">
      <form onSubmit={submit} className="relative z-[2] w-full max-w-sm bg-[#FAFAF7] p-8 sm:p-10">
        <img src="/logo-dark.png" alt="ROMACASA Intermediazioni Immobiliari" className="h-12 w-auto mb-2" data-testid="admin-login-logo" />
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2E2E2E] mb-8">Area Riservata</p>
        <label className="block mb-4">
          <span className={labelCls}>Email</span>
          <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin@romacasa.it" />
        </label>
        <label className="block mb-6">
          <span className={labelCls}>Password</span>
          <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
        </label>
        {error && <p data-testid="admin-login-error" className="text-sm text-red-700 mb-4">{error}</p>}
        <button type="submit" data-testid="admin-login-submit" disabled={busy} className="w-full bg-[#000000] hover:bg-[#000000] text-white py-3.5 text-sm font-medium transition-colors disabled:opacity-60">
          {busy ? "Accesso…" : "Accedi"}
        </button>
      </form>
    </div>
  );
};

const PropertyForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const geocode = async () => {
    const q = [f.address, f.zone, f.city || "Roma"].filter(Boolean).join(", ");
    if (!f.address && !f.zone) { toast.error("Scrivi prima indirizzo o zona"); return; }
    setGeoBusy(true);
    try {
      const r = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=1`);
      const d = await r.json();
      const feat = d.features?.[0];
      if (!feat) { toast.error("Indirizzo non trovato, aggiungi più dettagli"); return; }
      const [lng, lat] = feat.geometry.coordinates;
      setF((s) => ({ ...s, lat: String(lat), lng: String(lng) }));
      toast.success("Coordinate trovate sulla mappa");
    } catch (e) {
      toast.error("Servizio mappe non raggiungibile, riprova");
    } finally {
      setGeoBusy(false);
    }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/upload", fd);
        setF((s) => ({ ...s, images: [...s.images, r.data.url] }));
      }
      toast.success("Foto caricate");
    } catch (e) {
      toast.error("Errore nel caricamento foto");
    } finally {
      setUploading(false);
    }
  };

  const dragIdx = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null);

  // Tastiera nell'anteprima foto: Esc chiude, frecce scorrono
  useEffect(() => {
    if (previewIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPreviewIdx(null);
      if (e.key === "ArrowRight") setPreviewIdx((i) => (i + 1) % f.images.length);
      if (e.key === "ArrowLeft") setPreviewIdx((i) => (i - 1 + f.images.length) % f.images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewIdx, f.images.length]);

  const dropImg = (i) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    setDragOver(null);
    if (from === null || from === i) return;
    const arr = [...f.images];
    const [moved] = arr.splice(from, 1);
    arr.splice(i, 0, moved);
    set("images", arr);
  };

  const setCover = (i) => {
    if (i === 0) return;
    set("images", [f.images[i], ...f.images.filter((_, x) => x !== i)]);
    toast.success("Copertina aggiornata — ricordati di salvare");
  };

  const addUrl = () => {
    const url = window.prompt("Incolla l'URL dell'immagine:");
    if (url && url.trim()) set("images", [...f.images, url.trim()]);
  };

  const uploadPlan = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd);
      set("floor_plan", r.data.url);
      toast.success("Planimetria caricata");
    } catch (e) {
      toast.error("Errore nel caricamento della planimetria");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast.error("Inserisci un titolo"); return; }
    if (f.images.length === 0) { toast.error("Aggiungi almeno una foto"); return; }
    setBusy(true);
    try {
      await onSave(toPayload(f));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="admin-property-form" className="bg-white border border-[#E0E3E6] p-6 sm:p-8 mb-10">
      <h2 className="font-serif-d text-2xl font-medium mb-6">{f.id ? "Modifica immobile" : "Nuovo immobile"}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <label className="md:col-span-2"><span className={labelCls}>Titolo *</span>
          <input data-testid="form-title" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} required /></label>
        <label><span className={labelCls}>Badge (es. Esclusiva)</span>
          <input data-testid="form-badge" value={f.badge} onChange={(e) => set("badge", e.target.value)} className={inputCls} /></label>
        <label><span className={labelCls}>Contratto</span>
          <select data-testid="form-contract" value={f.contract} onChange={(e) => set("contract", e.target.value)} className={inputCls}>
            {CONTRACTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select></label>
        <label><span className={labelCls}>Tipologia</span>
          <select data-testid="form-category" value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select></label>
        <label><span className={labelCls}>Prezzo € (se affitto: canone mensile)</span>
          <input data-testid="form-price" type="number" min="0" value={f.price} onChange={(e) => set("price", e.target.value)} disabled={f.price_on_request} className={`${inputCls} disabled:opacity-40`} /></label>
        <label className="flex items-center gap-2.5 text-sm text-[#2E2E2E] cursor-pointer select-none">
          <input type="checkbox" data-testid="form-price-on-request" checked={!!f.price_on_request} onChange={(e) => set("price_on_request", e.target.checked)} className="w-4 h-4 accent-black" />
          Prezzo su richiesta <span className="text-[#8A94A0] text-xs">(sul sito appare solo "Prezzo su richiesta", senza cifra)</span>
        </label>
        <label><span className={labelCls}>Zona</span>
          <input data-testid="form-zone" value={f.zone} onChange={(e) => set("zone", e.target.value)} className={inputCls} placeholder="Es. Parioli" /></label>
        <label><span className={labelCls}>Città</span>
          <input data-testid="form-city" value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls} placeholder="Es. Roma, Venezia, Milano…" /></label>
        <label className="md:col-span-2"><span className={labelCls}>Indirizzo</span>
          <input data-testid="form-address" value={f.address} onChange={(e) => set("address", e.target.value)} className={inputCls} placeholder="Via …" /></label>
        <div className="flex items-end">
          <button type="button" data-testid="form-geocode-button" onClick={geocode} disabled={geoBusy}
            className="w-full flex items-center justify-center gap-2 border border-[#000000] text-[#000000] hover:bg-[#000000] hover:text-white px-4 py-3 text-sm font-medium transition-colors disabled:opacity-60">
            <MapPin size={15} /> {geoBusy ? "Cerco…" : "Trova coordinate"}
          </button>
        </div>
        <label><span className={labelCls}>Latitudine</span>
          <input data-testid="form-lat" type="number" step="any" value={f.lat} onChange={(e) => set("lat", e.target.value)} className={inputCls} placeholder="41.9…" /></label>
        <label><span className={labelCls}>Longitudine</span>
          <input data-testid="form-lng" type="number" step="any" value={f.lng} onChange={(e) => set("lng", e.target.value)} className={inputCls} placeholder="12.4…" /></label>
        <label><span className={labelCls}>Mq</span>
          <input data-testid="form-mq" type="number" min="0" value={f.mq} onChange={(e) => set("mq", e.target.value)} className={inputCls} /></label>
        <label><span className={labelCls}>Locali</span>
          <StepperInput testid="form-rooms" value={f.rooms} onChange={(v) => set("rooms", v)} /></label>
        <label><span className={labelCls}>Camere</span>
          <StepperInput testid="form-bedrooms" value={f.bedrooms} onChange={(v) => set("bedrooms", v)} /></label>
        <label><span className={labelCls}>Bagni</span>
          <StepperInput testid="form-bathrooms" value={f.bathrooms} onChange={(v) => set("bathrooms", v)} /></label>
        <label><span className={labelCls}>Piano</span>
          <input data-testid="form-floor" value={f.floor} onChange={(e) => set("floor", e.target.value)} className={inputCls} placeholder="Es. 3" /></label>
        <label><span className={labelCls}>Classe energetica</span>
          <input data-testid="form-energy" value={f.energy_class} onChange={(e) => set("energy_class", e.target.value)} className={inputCls} placeholder="Es. A2" /></label>
        <label><span className={labelCls}>Ascensore</span>
          <select data-testid="form-elevator" value={f.elevator} onChange={(e) => set("elevator", e.target.value)} className={inputCls}>
            <option value="">Non specificato</option><option value="si">Sì</option><option value="no">No</option>
          </select></label>
        <label><span className={labelCls}>Balcone</span>
          <select data-testid="form-balcony" value={f.balcony} onChange={(e) => set("balcony", e.target.value)} className={inputCls}>
            <option value="">Non specificato</option><option value="si">Sì</option><option value="no">No</option>
          </select></label>
        <label><span className={labelCls}>Terrazzo</span>
          <select data-testid="form-terrace" value={f.terrace} onChange={(e) => set("terrace", e.target.value)} className={inputCls}>
            <option value="">Non specificato</option><option value="si">Sì</option><option value="no">No</option>
          </select></label>
        <label><span className={labelCls}>Cucina (tipologia)</span>
          <input data-testid="form-kitchen" list="kitchen-options" value={f.kitchen} onChange={(e) => set("kitchen", e.target.value)} className={inputCls} placeholder="Es. Abitabile, angolo cottura…" />
          <datalist id="kitchen-options">{KITCHEN_OPTS.map((o) => <option key={o} value={o} />)}</datalist></label>
        <label><span className={labelCls}>Climatizzazione</span>
          <input data-testid="form-aircon" list="ac-options" value={f.air_conditioning} onChange={(e) => set("air_conditioning", e.target.value)} className={inputCls} placeholder="Es. Autonomo, freddo/caldo…" />
          <datalist id="ac-options">{AC_OPTS.map((o) => <option key={o} value={o} />)}</datalist></label>
        <label><span className={labelCls}>Riscaldamento</span>
          <input data-testid="form-heating" list="heating-options" value={f.heating} onChange={(e) => set("heating", e.target.value)} className={inputCls} placeholder="Es. Autonomo, a metano…" />
          <datalist id="heating-options">{HEATING_OPTS.map((o) => <option key={o} value={o} />)}</datalist></label>
        <label><span className={labelCls}>Stato dell'immobile</span>
          <select data-testid="form-condition" value={f.condition} onChange={(e) => set("condition", e.target.value)} className={inputCls}>
            <option value="">Non specificato</option>
            <option value="ristrutturato">Ristrutturato</option>
            <option value="buono_abitabile">Buono abitabile</option>
            <option value="da_ristrutturare">Da ristrutturare</option>
          </select></label>
        <label className="md:col-span-3"><span className={labelCls}>Descrizione</span>
          <textarea data-testid="form-description" rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></label>
        <label className="md:col-span-3"><span className={labelCls}>Caratteristiche (separate da virgola)</span>
          <input data-testid="form-features" value={f.features} onChange={(e) => set("features", e.target.value)} className={inputCls} placeholder="Terrazzo, Box auto, Domotica" /></label>
      </div>

      <div className="mt-6">
        <span className={labelCls}>Foto *</span>
        <p className="text-[11px] text-[#8A94A0] mb-2 -mt-0.5">Clicca una foto per vederla grande · trascinala nel punto desiderato per cambiare l'ordine · ★ per metterla in copertina · ✕ per eliminarla. La prima foto è sempre la copertina.</p>
        <div className="flex flex-wrap gap-3 items-start">
          {f.images.map((u, i) => (
            <div
              key={i}
              data-testid={`form-image-${i}`}
              draggable
              onDragStart={() => { dragIdx.current = i; }}
              onDragOver={(e) => { e.preventDefault(); if (dragOver !== i) setDragOver(i); }}
              onDragLeave={() => setDragOver((d) => (d === i ? null : d))}
              onDrop={(e) => { e.preventDefault(); dropImg(i); }}
              onDragEnd={() => { dragIdx.current = null; setDragOver(null); }}
              onClick={() => setPreviewIdx(i)}
              title="Clicca per ingrandire · trascina per spostare"
              className={`relative group cursor-grab active:cursor-grabbing transition-transform ${dragOver === i ? "ring-2 ring-black scale-105" : ""}`}
            >
              <img src={imgSrc(u)} alt={`Foto ${i + 1}`} draggable={false} className={`w-24 h-24 object-cover pointer-events-none ${i === 0 ? "border-2 border-black" : "border border-[#E0E3E6]"}`} />
              {i === 0 && <span className="absolute top-0 inset-x-0 bg-black text-white text-[9px] text-center py-0.5 font-semibold z-[1]">COPERTINA</span>}
              <span className="absolute top-1 right-1 bg-black/70 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><GripVertical size={12} /></span>
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1.5 bg-black/75 opacity-0 group-hover:opacity-100 transition-opacity py-1">
                <button type="button" title="Metti in copertina" data-testid={`form-img-cover-${i}`} onClick={(e) => { e.stopPropagation(); setCover(i); }} disabled={i === 0}
                  className="text-white disabled:opacity-30 px-1"><Star size={12} /></button>
                <button type="button" title="Elimina foto" data-testid={`form-remove-image-${i}`} onClick={(e) => { e.stopPropagation(); set("images", f.images.filter((_, x) => x !== i)); }}
                  className="text-white px-1"><X size={13} /></button>
              </div>
            </div>
          ))}
          <label data-testid="form-upload-button" className="w-24 h-24 border border-dashed border-[#000000] flex flex-col items-center justify-center gap-1 text-[#2E2E2E] cursor-pointer hover:bg-[#F1F2F4] transition-colors">
            <Upload size={18} />
            <span className="text-[10px] font-medium">{uploading ? "…" : "Carica"}</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files || []))} />
          </label>
          <button type="button" data-testid="form-add-url-button" onClick={addUrl} className="w-24 h-24 border border-dashed border-[#E0E3E6] text-[#5B6670] text-[10px] font-medium hover:border-[#000000] hover:text-[#2E2E2E] transition-colors">
            + URL
          </button>
        </div>
      </div>

      <div className="mt-6">
        <span className={labelCls}>Planimetria (immagine o PDF)</span>
        {f.floor_plan ? (
          <div className="flex items-center gap-4" data-testid="form-plan-preview">
            {f.floor_plan.toLowerCase().endsWith(".pdf") ? (
              <span className="flex items-center gap-2 border border-[#E0E3E6] bg-white px-4 py-3 text-sm"><FileText size={16} /> Planimetria PDF allegata</span>
            ) : (
              <img src={imgSrc(f.floor_plan)} alt="Planimetria" className="w-24 h-24 object-contain border border-[#E0E3E6] bg-white" />
            )}
            <button type="button" data-testid="form-remove-plan" onClick={() => set("floor_plan", "")} className="text-xs border border-red-200 text-red-700 hover:bg-red-50 px-3 py-2 transition-colors">Rimuovi</button>
          </div>
        ) : (
          <label data-testid="form-upload-plan" className="inline-flex items-center gap-2 border border-dashed border-black cursor-pointer px-5 py-3 text-sm hover:bg-[#F1F2F4] transition-colors">
            <Upload size={15} /> {uploading ? "Caricamento…" : "Carica planimetria"}
            <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => uploadPlan(e.target.files?.[0])} />
          </label>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input data-testid="form-featured" type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#000000] w-4 h-4" />
          In evidenza in home
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input data-testid="form-visible" type="checkbox" checked={f.visible} onChange={(e) => set("visible", e.target.checked)} className="accent-[#000000] w-4 h-4" />
          Visibile sul sito
        </label>
      </div>

      <div className="mt-8 flex gap-3">
        <button type="submit" data-testid="form-save-button" disabled={busy || uploading} className="bg-[#000000] hover:bg-[#000000] text-white px-8 py-3.5 text-sm font-medium transition-colors disabled:opacity-60">
          {busy ? "Salvataggio…" : "Salva immobile"}
        </button>
        <button type="button" data-testid="form-cancel-button" onClick={onCancel} className="border border-[#E0E3E6] hover:border-[#000000] px-8 py-3.5 text-sm font-medium transition-colors">
          Annulla
        </button>
      </div>

      {previewIdx !== null && f.images[previewIdx] && (
        <div data-testid="form-image-preview" onClick={() => setPreviewIdx(null)}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 sm:p-10 cursor-zoom-out">
          <button type="button" data-testid="preview-close" onClick={() => setPreviewIdx(null)} aria-label="Chiudi anteprima"
            className="absolute top-5 right-5 text-white/80 hover:text-white transition-colors z-10"><X size={30} /></button>
          <span className="absolute top-7 left-1/2 -translate-x-1/2 text-white/80 text-[11px] font-mono-d tracking-[0.2em]">
            FOTO {previewIdx + 1} DI {f.images.length}{previewIdx === 0 ? " · COPERTINA" : ""}
          </span>
          {f.images.length > 1 && (
            <>
              <button type="button" data-testid="preview-prev" onClick={(e) => { e.stopPropagation(); setPreviewIdx((i) => (i - 1 + f.images.length) % f.images.length); }} aria-label="Foto precedente"
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white transition-colors z-10"><ChevronLeft size={26} /></button>
              <button type="button" data-testid="preview-next" onClick={(e) => { e.stopPropagation(); setPreviewIdx((i) => (i + 1) % f.images.length); }} aria-label="Foto successiva"
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white transition-colors z-10"><ChevronRight size={26} /></button>
            </>
          )}
          <img src={imgSrc(f.images[previewIdx])} alt={`Anteprima foto ${previewIdx + 1}`} onClick={(e) => e.stopPropagation()} draggable={false}
            className="max-w-full max-h-full object-contain cursor-default select-none" />
        </div>
      )}
    </form>
  );
};

const emptyPost = { id: null, title: "", excerpt: "", content: "", cover: "", visible: true };

const PostsManager = () => {
  const [posts, setPosts] = useState([]);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => toast.error("Errore caricamento articoli"));
  useEffect(() => { load(); }, []);

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd);
      setF((s) => ({ ...s, cover: r.data.url }));
      toast.success("Copertina caricata");
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast.error("Inserisci un titolo"); return; }
    setBusy(true);
    try {
      const payload = { title: f.title, excerpt: f.excerpt, content: f.content, cover: f.cover || null, visible: f.visible };
      if (f.id) {
        await api.put(`/admin/posts/${f.id}`, payload);
        toast.success("Articolo aggiornato");
      } else {
        await api.post("/admin/posts", payload);
        toast.success("Articolo pubblicato");
      }
      setF(null);
      load();
    } catch (err) {
      toast.error("Errore nel salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare l'articolo "${p.title}"?`)) return;
    await api.delete(`/admin/posts/${p.id}`);
    toast.success("Articolo eliminato");
    load();
  };

  return (
    <div data-testid="posts-manager">
      {f ? (
        <form onSubmit={save} data-testid="admin-post-form" className="bg-white border border-[#E0E3E6] p-6 sm:p-8 mb-10">
          <h2 className="font-serif-d text-2xl font-medium mb-6">{f.id ? "Modifica articolo" : "Nuovo articolo"}</h2>
          <div className="grid grid-cols-1 gap-5">
            <label><span className={labelCls}>Titolo *</span>
              <input data-testid="post-form-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} required /></label>
            <label><span className={labelCls}>Anteprima breve (sottotitolo)</span>
              <input data-testid="post-form-excerpt" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} className={inputCls} /></label>
            <label><span className={labelCls}>Testo dell'articolo</span>
              <textarea data-testid="post-form-content" rows={10} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} className={inputCls} placeholder="Scrivi qui l'articolo…" /></label>
            <div>
              <span className={labelCls}>Immagine di copertina</span>
              {f.cover ? (
                <div className="flex items-center gap-4">
                  <img src={imgSrc(f.cover)} alt="Copertina" className="w-28 h-20 object-cover border border-[#E0E3E6]" />
                  <button type="button" data-testid="post-form-remove-cover" onClick={() => setF({ ...f, cover: "" })} className="text-xs border border-red-200 text-red-700 hover:bg-red-50 px-3 py-2 transition-colors">Rimuovi</button>
                </div>
              ) : (
                <label data-testid="post-form-upload-cover" className="inline-flex items-center gap-2 border border-dashed border-black cursor-pointer px-5 py-3 text-sm hover:bg-[#F1F2F4] transition-colors">
                  <Upload size={15} /> {uploading ? "Caricamento…" : "Carica copertina"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} />
                </label>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
              <input data-testid="post-form-visible" type="checkbox" checked={f.visible} onChange={(e) => setF({ ...f, visible: e.target.checked })} className="accent-black w-4 h-4" />
              Visibile sul sito
            </label>
          </div>
          <div className="mt-8 flex gap-3">
            <button type="submit" data-testid="post-form-save" disabled={busy || uploading} className="bg-black hover:bg-[#2E2E2E] text-white px-8 py-3.5 text-sm font-medium transition-colors disabled:opacity-60">
              {busy ? "Salvataggio…" : "Salva articolo"}
            </button>
            <button type="button" data-testid="post-form-cancel" onClick={() => setF(null)} className="border border-[#E0E3E6] hover:border-black px-8 py-3.5 text-sm font-medium transition-colors">
              Annulla
            </button>
          </div>
        </form>
      ) : (
        <button onClick={() => setF(emptyPost)} data-testid="admin-new-post-button"
          className="flex items-center gap-2 bg-black hover:bg-[#2E2E2E] text-white px-6 py-3.5 text-sm font-medium transition-colors mb-8">
          <Plus size={16} /> Nuovo articolo
        </button>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="admin-post-list">
        {posts.map((p) => (
          <div key={p.id} data-testid={`admin-post-${p.id}`} className={`bg-white border ${p.visible ? "border-[#E0E3E6]" : "border-dashed border-black"} overflow-hidden`}>
            {p.cover && <img src={imgSrc(p.cover)} alt={p.title} className="w-full h-36 object-cover" />}
            <div className="p-5">
              <p className="font-mono-d text-[10px] text-[#8A94A0] mb-2">{new Date(p.created_at).toLocaleDateString("it-IT")}{!p.visible ? " · BOZZA" : ""}</p>
              <h3 className="font-serif-d text-lg font-medium leading-snug">{p.title}</h3>
              <div className="mt-4 pt-4 border-t border-[#E0E3E6] flex items-center gap-2">
                <button onClick={() => setF({ ...emptyPost, ...p })} data-testid={`admin-post-edit-${p.id}`} className="flex items-center gap-1.5 text-xs border border-[#E0E3E6] hover:border-black px-3 py-2 transition-colors">
                  <Edit size={13} /> Modifica
                </button>
                <button onClick={() => remove(p)} data-testid={`admin-post-delete-${p.id}`} className="ml-auto flex items-center gap-1.5 text-xs border border-red-200 text-red-700 hover:bg-red-50 px-3 py-2 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FaqManager = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/faqs")
      .then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("Errore caricamento FAQ"))
      .finally(() => setLoaded(true));
  }, []);

  const set = (i, k, v) => setItems((arr) => arr.map((it, x) => (x === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((arr) => [...arr, { q: "", a: "", q_en: "", a_en: "", q_es: "", a_es: "" }]);
  const removeItem = (i) => {
    if (!window.confirm("Eliminare questa domanda?")) return;
    setItems((arr) => arr.filter((_, x) => x !== i));
  };
  const move = (i, dir) => setItems((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/faqs", { items });
      toast.success("FAQ salvate e pubblicate");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <div className="py-16 text-center text-[#5B6670]">Caricamento…</div>;

  return (
    <div data-testid="faq-manager">
      <div className="bg-[#FAFAF7] border border-[#E0E3E6] p-4 mb-8 text-xs text-[#5B6670] font-light leading-relaxed">
        <strong className="text-[#2E2E2E] font-medium">Come funziona:</strong> modifica domande e risposte in italiano (obbligatorie) e, se vuoi, le traduzioni in inglese e spagnolo.
        Se una traduzione manca, i visitatori stranieri vedono il testo italiano. Ricordati di premere "Salva tutte le FAQ" in fondo.
      </div>

      {items.map((it, i) => (
        <div key={i} data-testid={`faq-edit-${i}`} className="bg-white border border-[#E0E3E6] p-6 mb-5">
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono-d text-xs text-[#8A94A0]">Domanda {i + 1}</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => move(i, -1)} data-testid={`faq-up-${i}`} className="text-xs border border-[#E0E3E6] hover:border-black px-3 py-1.5 transition-colors">↑</button>
              <button type="button" onClick={() => move(i, 1)} data-testid={`faq-down-${i}`} className="text-xs border border-[#E0E3E6] hover:border-black px-3 py-1.5 transition-colors">↓</button>
              <button type="button" onClick={() => removeItem(i)} data-testid={`faq-delete-${i}`} className="text-xs border border-red-200 text-red-700 hover:bg-red-50 px-3 py-1.5 transition-colors"><Trash2 size={13} /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <label><span className={labelCls}>Domanda (IT) *</span>
              <input data-testid={`faq-q-${i}`} value={it.q} onChange={(e) => set(i, "q", e.target.value)} className={inputCls} /></label>
            <label><span className={labelCls}>Risposta (IT) *</span>
              <textarea data-testid={`faq-a-${i}`} rows={3} value={it.a} onChange={(e) => set(i, "a", e.target.value)} className={inputCls} /></label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label><span className={labelCls}>Domanda (EN)</span>
                <input data-testid={`faq-qen-${i}`} value={it.q_en || ""} onChange={(e) => set(i, "q_en", e.target.value)} className={inputCls} /></label>
              <label><span className={labelCls}>Risposta (EN)</span>
                <textarea data-testid={`faq-aen-${i}`} rows={2} value={it.a_en || ""} onChange={(e) => set(i, "a_en", e.target.value)} className={inputCls} /></label>
              <label><span className={labelCls}>Domanda (ES)</span>
                <input data-testid={`faq-qes-${i}`} value={it.q_es || ""} onChange={(e) => set(i, "q_es", e.target.value)} className={inputCls} /></label>
              <label><span className={labelCls}>Risposta (ES)</span>
                <textarea data-testid={`faq-aes-${i}`} rows={2} value={it.a_es || ""} onChange={(e) => set(i, "a_es", e.target.value)} className={inputCls} /></label>
            </div>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-3 mt-2">
        <button onClick={addItem} data-testid="faq-add-button" className="flex items-center gap-2 border border-dashed border-black hover:bg-[#F1F2F4] px-6 py-3.5 text-sm font-medium transition-colors">
          <Plus size={16} /> Aggiungi domanda
        </button>
        <button onClick={save} disabled={busy} data-testid="faq-save-button" className="bg-black hover:bg-[#2E2E2E] text-white px-8 py-3.5 text-sm font-medium transition-colors disabled:opacity-60">
          {busy ? "Salvataggio…" : "Salva tutte le FAQ"}
        </button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState("immobili");

  const load = () => api.get("/admin/properties").then((r) => setItems(r.data)).catch(() => toast.error("Errore di caricamento"));
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (editing?.id) {
        await api.put(`/admin/properties/${editing.id}`, payload);
        toast.success("Immobile aggiornato");
      } else {
        await api.post("/admin/properties", payload);
        toast.success("Immobile pubblicato");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const toggle = async (p, key) => {
    await api.put(`/admin/properties/${p.id}`, toPayload({ ...toForm(p), [key]: !p[key] }));
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare definitivamente "${p.title}"?`)) return;
    await api.delete(`/admin/properties/${p.id}`);
    toast.success("Immobile eliminato");
    load();
  };

  return (
    <div className="min-h-screen bg-[#F1F2F4] pt-8 pb-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <img src="/logo-dark.png" alt="ROMACASA" className="h-10 w-auto" data-testid="admin-dashboard-logo" />
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2E2E2E] mt-2">Gestione Immobili — {user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-translate-all"
              onClick={async () => {
                try {
                  await api.post("/admin/translate-content");
                  toast.success("Traduzioni EN/ES avviate: titoli, descrizioni, articoli e FAQ si aggiornano da soli in 1-2 minuti.");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Errore avvio traduzioni");
                }
              }}
              className="flex items-center gap-2 border border-[#E0E3E6] bg-white hover:border-[#000000] px-4 py-2.5 text-sm transition-colors"
              title="Traduci automaticamente tutti i contenuti in inglese e spagnolo"
            >
              <Languages size={15} className="text-[#000000]" /> Traduci tutto EN/ES
            </button>
            <a href={`${BACKEND_URL}/api/feed.xml`} target="_blank" rel="noreferrer" data-testid="admin-feed-link"
              className="flex items-center gap-2 border border-[#E0E3E6] bg-white hover:border-[#000000] px-4 py-2.5 text-sm transition-colors" title="Feed XML per immobiliare.it / idealista">
              <Rss size={15} className="text-[#000000]" /> Feed Portali
            </a>
            <a href="/" data-testid="admin-view-site" className="border border-[#E0E3E6] bg-white hover:border-[#000000] px-4 py-2.5 text-sm transition-colors">Vedi sito</a>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 bg-[#000000] hover:bg-[#000000] text-white px-4 py-2.5 text-sm transition-colors">
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          <button data-testid="admin-tab-immobili" onClick={() => setTab("immobili")}
            className={tab === "immobili" ? "bg-black text-white px-6 py-2.5 text-sm font-medium transition-colors" : "border border-[#E0E3E6] bg-white hover:border-black px-6 py-2.5 text-sm font-medium transition-colors"}>
            Immobili
          </button>
          <button data-testid="admin-tab-blog" onClick={() => setTab("blog")}
            className={tab === "blog" ? "bg-black text-white px-6 py-2.5 text-sm font-medium transition-colors" : "border border-[#E0E3E6] bg-white hover:border-black px-6 py-2.5 text-sm font-medium transition-colors"}>
            Articoli Blog
          </button>
          <button data-testid="admin-tab-faq" onClick={() => setTab("faq")}
            className={tab === "faq" ? "bg-black text-white px-6 py-2.5 text-sm font-medium transition-colors" : "border border-[#E0E3E6] bg-white hover:border-black px-6 py-2.5 text-sm font-medium transition-colors"}>
            FAQ
          </button>
        </div>

        {tab === "blog" ? (
          <PostsManager />
        ) : tab === "faq" ? (
          <FaqManager />
        ) : (
        <>
        <div className="bg-[#FAFAF7] border border-[#E0E3E6] p-4 mb-8 text-xs text-[#5B6670] font-light leading-relaxed">
          <strong className="text-[#2E2E2E] font-medium">Sincronizzazione portali:</strong> per pubblicare automaticamente gli annunci anche su immobiliare.it e idealista,
          comunica ai portali questo feed XML — <span className="font-mono-d">{BACKEND_URL}/api/feed.xml</span> — si aggiorna da solo a ogni immobile che salvi qui.
        </div>

        {showForm ? (
          <PropertyForm initial={editing ? toForm(editing) : emptyForm} onSave={save} onCancel={() => { setShowForm(false); setEditing(null); }} />
        ) : (
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="admin-new-property-button"
            className="flex items-center gap-2 bg-[#000000] hover:bg-[#2E2E2E] text-white px-6 py-3.5 text-sm font-medium transition-colors mb-8">
            <Plus size={16} /> Nuovo immobile
          </button>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="admin-property-list">
          {items.map((p) => (
            <div key={p.id} data-testid={`admin-card-${p.id}`} className={`bg-white border ${p.visible ? "border-[#E0E3E6]" : "border-dashed border-[#000000]"} overflow-hidden`}>
              <div className="relative">
                <img src={imgSrc(p.images?.[0])} alt={p.title} className="w-full h-44 object-cover" />
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="bg-[#000000]/85 text-[#FAFAF7] text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1">{contractLabel(p.contract)}</span>
                  <span className="bg-[#000000] text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1">{categoryLabel(p.category)}</span>
                  {!p.visible && <span className="bg-red-800 text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1">Nascosto</span>}
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-serif-d text-lg font-medium leading-snug">{p.title}</h3>
                  <span className="font-mono-d text-[10px] text-[#8A94A0] shrink-0">{p.ref}</span>
                </div>
                <p className="text-xs text-[#5B6670] mt-1">{p.zone} · {p.city || "Roma"} · {p.mq || "—"} mq</p>
                <p className="font-mono-d text-sm font-semibold text-[#000000] mt-2">{formatPrice(p)}</p>
                <div className="mt-4 pt-4 border-t border-[#E0E3E6] flex items-center gap-2">
                  <button onClick={() => { setEditing(p); setShowForm(true); }} data-testid={`admin-edit-${p.id}`} className="flex items-center gap-1.5 text-xs border border-[#E0E3E6] hover:border-[#000000] px-3 py-2 transition-colors">
                    <Edit size={13} /> Modifica
                  </button>
                  <button onClick={() => toggle(p, "visible")} data-testid={`admin-toggle-visible-${p.id}`} className="flex items-center gap-1.5 text-xs border border-[#E0E3E6] hover:border-[#000000] px-3 py-2 transition-colors">
                    {p.visible ? <><EyeOff size={13} /> Nascondi</> : <><Eye size={13} /> Mostra</>}
                  </button>
                  <button onClick={() => toggle(p, "featured")} data-testid={`admin-toggle-featured-${p.id}`} className={`flex items-center gap-1.5 text-xs border px-3 py-2 transition-colors ${p.featured ? "border-[#000000] text-[#2E2E2E]" : "border-[#E0E3E6] hover:border-[#000000]"}`}>
                    <Star size={13} fill={p.featured ? "#000000" : "none"} />
                  </button>
                  <button onClick={() => remove(p)} data-testid={`admin-delete-${p.id}`} className="ml-auto flex items-center gap-1.5 text-xs border border-red-200 text-red-700 hover:bg-red-50 px-3 py-2 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

const AdminPage = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#000000] flex items-center justify-center text-[#8A94A0]" data-testid="admin-loading">Caricamento…</div>;
  return (
    <>
      <Seo title="Area Riservata | ROMACASA" siteName="ROMACASA" description="Area riservata ROMACASA." />
      {user ? <Dashboard /> : <LoginForm />}
    </>
  );
};

export default AdminPage;
